/**
 * Permissoes do modulo WhatsApp.
 *
 * Estrutura por sub-modulo:
 *   /whatsapp/chat      — Acoes de atendimento (assumir, transferir, responder, etc.)
 *   /whatsapp/kanban     — Acoes no Kanban de tarefas
 *   /whatsapp/contatos   — Acoes de contatos
 *
 * Cada sub-modulo tem suas acoes granulares que sao configuradas
 * no backend (tabela de telas/permissoes) e atribuidas ao grupo do usuario.
 *
 * Uso: hasPermission('/whatsapp/chat', 'assumir_fila')
 */

// Paths dos sub-modulos (devem bater com os paths das rotas em private.routes.ts)
export const WA_PATHS = {
  CHAT: '/whatsapp',
  KANBAN: '/whatsapp/kanban',
  CONTATOS: '/whatsapp/clientes',
} as const;

// Path base (para checagem de existencia de qualquer permissao WA)
export const WA_PERMISSION_PATH = '/whatsapp';

// ─── Acoes de Chat/Atendimento ──────────────────────────────────────
export const WA_CHAT_ACTIONS = {
  /** Assumir chamados da fila (nao atribuidos) */
  ASSUMIR_FILA: 'assumir_fila',
  /** Assumir chamado que esta com outro atendente do mesmo departamento */
  ASSUMIR_DEPARTAMENTO: 'assumir_departamento',
  /** Assumir qualquer chamado (inclusive de outros departamentos) */
  ASSUMIR_TODOS: 'assumir_todos',

  /** Transferir chamados proprios */
  TRANSFERIR_PROPRIO: 'transferir_proprio',
  /** Transferir chamados do mesmo departamento */
  TRANSFERIR_DEPARTAMENTO: 'transferir_departamento',
  /** Transferir qualquer chamado */
  TRANSFERIR_TODOS: 'transferir_todos',

  /** Enviar mensagens ao cliente */
  RESPONDER: 'responder',
  /** Adicionar notas internas */
  NOTA_INTERNA: 'nota_interna',
  /** Finalizar/encerrar um chamado */
  FINALIZAR: 'finalizar',
  /** Abrir novo chamado */
  ABRIR_CHAMADO: 'abrir_chamado',
} as const;

// ─── Acoes de Kanban ────────────────────────────────────────────────
export const WA_KANBAN_ACTIONS = {
  /** Visualizar tarefas */
  VISUALIZAR: 'visualizar',
  /** Criar tarefas */
  CRIAR: 'criar',
  /** Editar tarefas proprias */
  EDITAR_PROPRIO: 'editar_proprio',
  /** Editar qualquer tarefa */
  EDITAR_TODOS: 'editar_todos',
  /** Excluir tarefas */
  EXCLUIR: 'excluir',
  /** Mover tarefas entre colunas */
  MOVER: 'mover',
} as const;

// ─── Acoes de Contatos ──────────────────────────────────────────────
export const WA_CONTATOS_ACTIONS = {
  /** Visualizar contatos */
  VISUALIZAR: 'visualizar',
  /** Criar contatos */
  CRIAR: 'criar',
  /** Editar contatos */
  EDITAR: 'editar',
  /** Excluir contatos */
  EXCLUIR: 'excluir',
  /** Iniciar conversa com contato */
  INICIAR_CONVERSA: 'iniciar_conversa',
} as const;

// ─── Mapa legivel de acoes (para tela de configuracao) ──────────────
export const WA_ACTION_LABELS: Record<string, Record<string, string>> = {
  [WA_PATHS.CHAT]: {
    [WA_CHAT_ACTIONS.ASSUMIR_FILA]: 'Assumir chamados da fila',
    [WA_CHAT_ACTIONS.ASSUMIR_DEPARTAMENTO]: 'Assumir chamados do departamento',
    [WA_CHAT_ACTIONS.ASSUMIR_TODOS]: 'Assumir qualquer chamado',
    [WA_CHAT_ACTIONS.TRANSFERIR_PROPRIO]: 'Transferir chamados proprios',
    [WA_CHAT_ACTIONS.TRANSFERIR_DEPARTAMENTO]: 'Transferir chamados do departamento',
    [WA_CHAT_ACTIONS.TRANSFERIR_TODOS]: 'Transferir qualquer chamado',
    [WA_CHAT_ACTIONS.RESPONDER]: 'Enviar mensagens ao cliente',
    [WA_CHAT_ACTIONS.NOTA_INTERNA]: 'Adicionar notas internas',
    [WA_CHAT_ACTIONS.FINALIZAR]: 'Finalizar chamados',
    [WA_CHAT_ACTIONS.ABRIR_CHAMADO]: 'Abrir chamados',
  },
  [WA_PATHS.KANBAN]: {
    [WA_KANBAN_ACTIONS.VISUALIZAR]: 'Visualizar tarefas',
    [WA_KANBAN_ACTIONS.CRIAR]: 'Criar tarefas',
    [WA_KANBAN_ACTIONS.EDITAR_PROPRIO]: 'Editar tarefas proprias',
    [WA_KANBAN_ACTIONS.EDITAR_TODOS]: 'Editar qualquer tarefa',
    [WA_KANBAN_ACTIONS.EXCLUIR]: 'Excluir tarefas',
    [WA_KANBAN_ACTIONS.MOVER]: 'Mover tarefas entre colunas',
  },
  [WA_PATHS.CONTATOS]: {
    [WA_CONTATOS_ACTIONS.VISUALIZAR]: 'Visualizar contatos',
    [WA_CONTATOS_ACTIONS.CRIAR]: 'Criar contatos',
    [WA_CONTATOS_ACTIONS.EDITAR]: 'Editar contatos',
    [WA_CONTATOS_ACTIONS.EXCLUIR]: 'Excluir contatos',
    [WA_CONTATOS_ACTIONS.INICIAR_CONVERSA]: 'Iniciar conversa com contato',
  },
};

// Backward compat — usado pelo hook antigo
export const WA_ACTIONS = WA_CHAT_ACTIONS;
export type WaAction = string;
